import { useState } from "react";
import * as Yup from "yup";
import api from "../../services/api";

const bankSchema = Yup.object({
  accountHolder: Yup.string().trim().required("Account holder name is required"),
  accountNumber: Yup.string().matches(/^\d+$/, "Account number must be numeric").required("Account number is required"),
  ifscSwift: Yup.string().min(11, "IFSC must be 11 characters").required("IFSC code is required"),
});

const banks = [
  "State Bank of India",
  "HDFC Bank",
  "ICICI Bank",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Punjab National Bank",
  "Bank of Baroda",
  "Canara Bank",
  "Union Bank of India",
  "IndusInd Bank",
];

export default function AddBankAccount({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    accountHolder: "",
    bankStatement: null,
    bankName: "",
    accountNumber: "",
    ifscSwift: "",
  });
  const [fileName, setFileName] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [bankVerified, setBankVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "accountNumber" || name === "ifscSwift" || name === "accountHolder") {
      setBankVerified(false);
      setVerifyMsg(null);
    }
  };

  const validate = async () => {
    try {
      await bankSchema.validate({ accountHolder: form.accountHolder, accountNumber: form.accountNumber, ifscSwift: form.ifscSwift }, { abortEarly: false });
      return {};
    } catch (err) {
      const errs = {};
      err.inner.forEach((e) => { errs[e.path] = e.message; });
      return errs;
    }
  };

  const verifyBank = async () => {
    const errs = await validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setVerifying(true);
    setVerifyMsg(null);
    try {
      const res = await api.post("/bank-account/verify", {
        account_number: form.accountNumber,
        ifsc_code: form.ifscSwift,
        name: form.accountHolder,
      });
      // IMPORTANT: check data.verified, NOT success
      const verified = res.data?.data?.verified;
      if (verified === true) {
        setBankVerified(true);
        setVerifyMsg({ type: "success", text: "Bank account verified successfully ✅" });
      } else {
        setBankVerified(false);
        setVerifyMsg({ type: "error", text: "Invalid account details. Verification failed." });
      }
    } catch (err) {
      setBankVerified(false);
      setVerifyMsg({ type: "error", text: err.response?.data?.message || "Bank verification failed." });
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async () => {
    if (!bankVerified) {
      setVerifyMsg({ type: "error", text: "Please verify your bank account first." });
      return;
    }
    const errs = {};
    if (!form.bankStatement) errs.bankStatement = "Please upload a bank statement or cancel cheque";
    if (!form.bankName) errs.bankName = "Please select a bank";
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setSuccessMsg(null);
    try {
      const fd = new FormData();
      fd.append("account_holder_name", form.accountHolder);
      fd.append("account_number", form.accountNumber);
      fd.append("bank_name", form.bankName);
      fd.append("ifsc_code", form.ifscSwift);
      if (form.bankStatement) fd.append("bank_statement", form.bankStatement);
      await api.post("/bank-account/submit", fd);
      setSuccessMsg("Bank account added successfully! ✅");
      setTimeout(() => { if (onSuccess) onSuccess(); else if (onClose) onClose(); }, 1500);
    } catch (err) {
      setErrors({ submit: err.response?.data?.message || "Failed to add account." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#FFFFFF] rounded-2xl overflow-hidden shadow-2xl">

        {/* Header */}
        <div className="bg-[linear-gradient(104.41deg,#754F89_0%,#4B2364_100%)] p-[29px] flex items-start justify-between">
          <div>
            <h2 className="text-[#FFFFFF] text-[28px] font-bold leading-snug">Add New Bank Account</h2>
            <p className="text-[#F3E8FFB2] text-[16px] mt-0.5">Whitelist a new institutional settlement account</p>
          </div>
          <button
            onClick={onClose}
            className="text-white bg-white/20 hover:bg-white/30 transition rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold flex-shrink-0 ml-4 mt-0.5 border-none cursor-pointer"
          >
            ×
          </button>
        </div>

        {/* Form Body */}
        <div className="px-[29px] py-[26px] space-y-4">

          {/* Account Holder Name */}
          <div>
            <label className="block text-[12px] font-semibold tracking-widest text-[#4C444E] uppercase mb-1.5">
              Account Holder Name
            </label>
            <div className="relative">
              <input
                type="text"
                name="accountHolder"
                value={form.accountHolder}
                onChange={handleChange}
                placeholder="e.g. Krestapay Global Operations LLC"
                className="w-full bg-[#F3F4F5] rounded-[12px] px-4 py-3 pr-10 text-sm text-gray-700 placeholder-[#7D747F] outline-none border border-transparent focus:border-purple-400 transition"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7D747F]">
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </span>
            </div>
            {errors.accountHolder && <p className="text-red-500 text-xs mt-1">{errors.accountHolder}</p>}
          </div>

          {/* Upload Bank Statement */}
          <div>
            <label className="block text-[12px] font-semibold tracking-widest text-[#4C444E] uppercase mb-1.5">
              Upload Bank Statement or Cancel Cheque
            </label>
            <label className="flex items-center justify-between bg-[#F3F4F5] rounded-xl px-4 py-3 cursor-pointer hover:bg-[#E5E7EB] transition border border-transparent">
              <span className="text-sm text-[#7D747F]">{fileName || "Upload"}</span>
              <span className="text-[#7D747F]">
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </span>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { const f = e.target.files[0]; if (f) { setForm((prev) => ({ ...prev, bankStatement: f })); setFileName(f.name); }}} />
            </label>
            {errors.bankStatement && <p className="text-red-500 text-xs mt-1">{errors.bankStatement}</p>}
          </div>

          {/* Bank Name */}
          <div>
            <label className="block text-[12px] font-semibold tracking-widest text-[#4C444E] uppercase mb-1.5">
              Bank Name
            </label>
            <div className="relative">
              <input
                  type="text"
                  name="bankName"
                  value={form.bankName}
                  onChange={handleChange}
                  list="banks-list"
                  placeholder="Select or type bank name"
                  className="w-full bg-[#F3F4F5] rounded-xl px-4 py-3 text-sm text-gray-700 outline-none border border-transparent focus:border-purple-400 transition"
                />

                <datalist id="banks-list">
                  {banks.map((b) => (
                    <option key={b} value={b} />
                  ))}
                </datalist>
            </div>
            {errors.bankName && <p className="text-red-500 text-xs mt-1">{errors.bankName}</p>}
          </div>

          {/* Account Number + IFSC + Verify */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-[12px] font-semibold tracking-widest text-[#4C444E] uppercase mb-1.5">
                Account Number
              </label>
              <input
                type="text"
                name="accountNumber"
                value={form.accountNumber}
                onChange={handleChange}
                placeholder="0000 0000 0000"
                className="w-full bg-[#F3F4F5] rounded-xl px-4 py-3 text-sm text-gray-700 placeholder:text-[#7D747F] outline-none border border-transparent focus:border-purple-400 transition"
              />
              {errors.accountNumber && <p className="text-red-500 text-xs mt-1">{errors.accountNumber}</p>}
            </div>
            <div className="flex-1">
              <label className="block text-[12px] font-semibold tracking-widest text-[#4C444E] uppercase mb-1.5">
                IFSC / Swift Code
              </label>
              <input
                type="text"
                name="ifscSwift"
                value={form.ifscSwift}
                onChange={handleChange}
                placeholder="KRSB0001234"
                className="w-full bg-[#F3F4F5] rounded-xl px-4 py-3 text-sm text-gray-700 placeholder:text-[#7D747F] outline-none border border-transparent focus:border-purple-400 transition"
              />
              {errors.ifscSwift && <p className="text-red-500 text-xs mt-1">{errors.ifscSwift}</p>}
            </div>
          </div>
          
          {/* Verify Bank Button */}
          <div>
            <button
              type="button"
              onClick={verifyBank}
              disabled={bankVerified || verifying}
              className={`w-full py-3 rounded-[12px] text-[13px] font-bold text-white transition-all
                ${ bankVerified
                  ? "bg-green-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-orange-500 to-red-600 hover:opacity-90 cursor-pointer"
                } disabled:opacity-70`}
            >
              {verifying ? "Verifying..." : bankVerified ? "Verified ✅" : "Verify Bank Account"}
            </button>
            {verifyMsg && (
              <p className={`mt-1.5 text-[12px] font-medium ${ verifyMsg.type === "success" ? "text-green-600" : "text-red-500" }`}>
                {verifyMsg.text}
              </p>
            )}
          </div>

          {successMsg && (
            <p className="text-green-600 text-[13px] font-semibold text-center">{successMsg}</p>
          )}
          {errors.submit && (
            <p className="text-red-500 text-[13px] font-semibold text-center">{errors.submit}</p>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-[16px] bg-[#EDEEEF] text-[#4C444E] font-semibold text-[16px] active:scale-95 transition-all cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!bankVerified || submitting}
              className="flex-1 py-3 rounded-[16px] bg-[linear-gradient(104.95deg,#EA8534_0%,#A0111A_100%)] text-white font-semibold text-sm hover:opacity-90 active:scale-95 transition-all shadow-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Adding..." : "Add Account"}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
